import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { extractYoutubeId } from './utils';

const TutorialFloatingPlayer = ({ video, isMinimized, onClose, onToggleMinimize }) => {
  const { t } = useTranslation();
  const playerRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!video) return null;

  const videoId = extractYoutubeId(video.youtube_url);
  if (!videoId) return null;

  const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1&modestbranding=1&showinfo=0&iv_load_policy=3`;

  return createPortal(
    <div className="tutorial-floating-player" ref={playerRef}>
      {/* Header bar */}
      <div className="tutorial-floating-player__header">
        <span className="tutorial-floating-player__title" title={video.label}>
          {video.label}
        </span>
        <div className="tutorial-floating-player__controls">
          <button
            type="button"
            className="tutorial-floating-player__btn"
            onClick={onToggleMinimize}
            aria-label={isMinimized ? t('common|expand_player', 'Agrandir') : t('common|minimize_player', 'Réduire')}
          >
            {isMinimized ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="tutorial-floating-player__btn tutorial-floating-player__btn--close"
            onClick={onClose}
            aria-label={t('common|close', 'Fermer')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Video iframe (hidden when minimized) */}
      {!isMinimized && (
        <div className="tutorial-floating-player__video">
          <iframe
            src={embedUrl}
            title={video.label}
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
    </div>,
    document.body
  );
};

export default TutorialFloatingPlayer;
